/**
 * Utilitário centralizado para ícones de plataformas de redes sociais
 * Garante consistência em toda a aplicação
 */

import { FaGlobe, FaLinkedin, FaYoutube } from "react-icons/fa";
import {
  SiFacebook,
  SiGoogle,
  SiInstagram,
  SiMeta,
  SiOpenai,
  SiTiktok,
} from "react-icons/si";
import React from "react";

export type PlatformId =
  | "linkedin"
  | "facebook"
  | "instagram"
  | "instagram_stories"
  | "facebook_stories"
  | "google_my_business"
  | "google_ads"
  | "google_services"
  | "youtube"
  | "tiktok"
  | "meta"
  | "chatgpt_ads"
  | "direct";

export interface PlatformIconProps {
  platform: PlatformId | string;
  className?: string;
  size?: number;
}

/**
 * Componente de ícone de plataforma padronizado
 * Sincronizado com os ícones usados na página de Organização (SiMeta, SiGoogle)
 */
export const PlatformIcon: React.FC<PlatformIconProps> = ({
  platform,
  className = "h-4 w-4",
  size,
}) => {
  const iconSize = size || 16;
  const iconClassName = size ? undefined : className;

  const normalizedPlatform = platform?.toLowerCase().trim();

  switch (normalizedPlatform) {
    case "linkedin":
      return <FaLinkedin className={iconClassName} size={iconSize} />;
    case "facebook":
    case "meta":
      return <SiMeta className={iconClassName} size={iconSize} />;
    case "instagram":
    case "instagram_stories":
      return <SiInstagram className={iconClassName} size={iconSize} />;
    case "google_my_business":
    case "google_ads":
    case "google_services":
    case "google my business":
    case "gmb":
      return <SiGoogle className={iconClassName} size={iconSize} />;
    case "youtube":
      return <FaYoutube className={iconClassName} size={iconSize} />;
    case "tiktok":
      return <SiTiktok className={iconClassName} size={iconSize} />;
    case "chatgpt_ads":
      return <SiOpenai className={iconClassName} size={iconSize} />;
    case "messenger":
      return <SiFacebook className={iconClassName} size={iconSize} />;
    case "direct":
      return <FaGlobe className={iconClassName} size={iconSize} />;
    default:
      // Fallback para plataforma desconhecida
      return <FaLinkedin className={iconClassName} size={iconSize} />;
  }
};

/**
 * Função utilitária para obter o componente de ícone
 */
export const getPlatformIcon = (
  platform: PlatformId | string,
  className?: string,
  size?: number,
): React.ReactElement => {
  return <PlatformIcon platform={platform} className={className} size={size} />;
};

/**
 * Função utilitária para obter apenas o componente (sem wrapper)
 * Útil para usar diretamente em JSX
 */
export const getPlatformIconComponent = (_platform: PlatformId | string) => {
  return PlatformIcon;
};

/**
 * Configurações de plataformas padronizadas
 */
export const PLATFORM_CONFIG = {
  linkedin: {
    id: "linkedin" as const,
    name: "LinkedIn",
    icon: FaLinkedin,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    hoverBg: "hover:bg-blue-100",
    borderColor: "border-blue-200",
  },
  facebook: {
    id: "facebook" as const,
    name: "Facebook",
    icon: SiFacebook,
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    hoverBg: "hover:bg-blue-100",
    borderColor: "border-blue-200",
  },
  instagram: {
    id: "instagram" as const,
    name: "Instagram",
    icon: SiInstagram,
    color: "text-pink-600",
    bgColor: "bg-pink-50",
    hoverBg: "hover:bg-pink-100",
    borderColor: "border-pink-200",
  },
  instagram_stories: {
    id: "instagram_stories" as const,
    name: "Instagram Stories",
    icon: SiInstagram,
    color: "text-pink-600",
    bgColor: "bg-pink-50",
    hoverBg: "hover:bg-pink-100",
    borderColor: "border-pink-200",
  },
  facebook_stories: {
    id: "facebook_stories" as const,
    name: "Facebook Stories",
    icon: SiFacebook,
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    hoverBg: "hover:bg-blue-100",
    borderColor: "border-blue-200",
  },
  messenger: {
    id: "messenger" as const,
    name: "Messenger",
    icon: SiFacebook,
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    hoverBg: "hover:bg-blue-100",
    borderColor: "border-blue-200",
  },
  google: {
    id: "google_my_business" as const,
    name: "Google My Business",
    icon: SiGoogle,
    color: "text-green-600",
    bgColor: "bg-green-50",
    hoverBg: "hover:bg-green-100",
    borderColor: "border-green-200",
  },
  google_ads: {
    id: "google_ads" as const,
    name: "Google Ads",
    icon: SiGoogle,
    color: "text-amber-500",
    bgColor: "bg-amber-50",
    hoverBg: "hover:bg-amber-100",
    borderColor: "border-amber-200",
  },
  direct: {
    id: "direct" as const,
    name: "Site / Direto",
    icon: FaGlobe,
    color: "text-slate-500",
    bgColor: "bg-slate-50",
    hoverBg: "hover:bg-slate-100",
    borderColor: "border-slate-200",
  },
  youtube: {
    id: "youtube" as const,
    name: "YouTube",
    icon: FaYoutube,
    color: "text-red-600",
    bgColor: "bg-red-50",
    hoverBg: "hover:bg-red-100",
    borderColor: "border-red-200",
  },
  tiktok: {
    id: "tiktok" as const,
    name: "TikTok",
    icon: SiTiktok,
    color: "text-black",
    bgColor: "bg-gray-50",
    hoverBg: "hover:bg-gray-100",
    borderColor: "border-gray-200",
  },
  chatgpt_ads: {
    id: "chatgpt_ads" as const,
    name: "ChatGPT Ads",
    icon: SiOpenai,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    hoverBg: "hover:bg-emerald-100",
    borderColor: "border-emerald-200",
  },
} as const;

/**
 * Lista de plataformas na ordem padrão
 */
export const PLATFORMS_LIST = [
  PLATFORM_CONFIG.linkedin,
  PLATFORM_CONFIG.facebook,
  PLATFORM_CONFIG.facebook_stories,
  PLATFORM_CONFIG.instagram,
  PLATFORM_CONFIG.instagram_stories,
  PLATFORM_CONFIG.google,
  PLATFORM_CONFIG.youtube,
  PLATFORM_CONFIG.tiktok,
] as const;

/**
 * Função para obter configuração de uma plataforma
 */
export const getPlatformConfig = (platform: PlatformId | string) => {
  if (!platform) return PLATFORM_CONFIG.linkedin;

  const normalizedPlatform = platform.toLowerCase().trim();

  // Aliases
  if (
    normalizedPlatform === "google_my_business" ||
    normalizedPlatform === "gmb" ||
    normalizedPlatform === "google_profile"
  ) {
    return PLATFORM_CONFIG.google;
  }
  if (normalizedPlatform === "meta") {
    return PLATFORM_CONFIG.facebook;
  }

  return (
    PLATFORM_CONFIG[normalizedPlatform as keyof typeof PLATFORM_CONFIG] ||
    PLATFORM_CONFIG.linkedin
  );
};

/**
 * Função para obter o nome legível da plataforma (nunca retorna snake_case)
 */
export const getPlatformName = (platform: PlatformId | string): string => {
  if (!platform) return "";

  const normalized = String(platform).toLowerCase().trim();

  if (
    normalized === "google_my_business" ||
    normalized === "gmb" ||
    normalized === "google_profile" ||
    normalized === "google"
  ) {
    return PLATFORM_CONFIG.google.name;
  }
  if (normalized === "meta") {
    return PLATFORM_CONFIG.facebook.name;
  }

  const known = PLATFORM_CONFIG[normalized as keyof typeof PLATFORM_CONFIG];
  if (known) {
    return known.name;
  }

  // Fallback: humaniza snake_case / kebab-case (ex.: google_ads → Google Ads)
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

/**
 * Função para obter a cor da plataforma
 */
export const getPlatformColor = (platform: PlatformId | string): string => {
  return getPlatformConfig(platform).color;
};

/**
 * Classes de um "chip" selecionável por plataforma (estado normal e
 * selecionado). Tailwind exige classes literais no código-fonte (JIT não
 * resolve `bg-${cor}-600` gerado em runtime), então isso fica como um mapa
 * estático — mas centralizado aqui, em vez de duplicado em cada modal que
 * precisa de um seletor de plataformas (ex.: CreatePostModal,
 * CreateContentModal).
 */
export const PLATFORM_CHIP_CLASSES: Record<
  string,
  { base: string; active: string }
> = {
  linkedin: {
    base: "border-blue-200 bg-blue-50 text-blue-700",
    active: "border-blue-600 bg-blue-600 text-white shadow-blue-200",
  },
  facebook: {
    base: "border-indigo-200 bg-indigo-50 text-indigo-700",
    active: "border-indigo-600 bg-indigo-600 text-white shadow-indigo-200",
  },
  facebook_stories: {
    base: "border-indigo-200 bg-indigo-50 text-indigo-700",
    active: "border-indigo-600 bg-indigo-600 text-white shadow-indigo-200",
  },
  messenger: {
    base: "border-blue-200 bg-blue-50 text-blue-700",
    active: "border-blue-600 bg-blue-600 text-white shadow-blue-200",
  },
  instagram: {
    base: "border-pink-200 bg-pink-50 text-pink-700",
    active:
      "border-pink-600 bg-linear-to-br from-pink-500 to-orange-400 text-white shadow-pink-200",
  },
  instagram_stories: {
    base: "border-pink-200 bg-pink-50 text-pink-700",
    active:
      "border-pink-600 bg-linear-to-br from-pink-500 to-orange-400 text-white shadow-pink-200",
  },
  google: {
    base: "border-green-200 bg-green-50 text-green-700",
    active: "border-green-500 bg-green-500 text-white shadow-green-200",
  },
  google_my_business: {
    base: "border-green-200 bg-green-50 text-green-700",
    active: "border-green-500 bg-green-500 text-white shadow-green-200",
  },
  google_ads: {
    base: "border-amber-200 bg-amber-50 text-amber-700",
    active: "border-amber-500 bg-amber-500 text-white shadow-amber-200",
  },
  youtube: {
    base: "border-red-200 bg-red-50 text-red-700",
    active: "border-red-600 bg-red-600 text-white shadow-red-200",
  },
  tiktok: {
    base: "border-gray-300 bg-gray-50 text-gray-700",
    active: "border-black bg-black text-white shadow-gray-300",
  },
  chatgpt_ads: {
    base: "border-emerald-200 bg-emerald-50 text-emerald-700",
    active: "border-emerald-600 bg-emerald-600 text-white shadow-emerald-200",
  },
};

export const getPlatformChipClasses = (platform: PlatformId | string) =>
  PLATFORM_CHIP_CLASSES[platform] || PLATFORM_CHIP_CLASSES.linkedin;
